<template>
  <teleport v-if="isImageModalOpen" to="body">
    <transition name="fade">
      <div
        v-closeOnPressEsc="closeModal"
        class="fixed top-0 left-0 p-10 md:p-15 w-full h-full bg-image-modal flex justify-center items-center z-100"
        @click.self="closeModal"
      >
        <!-- I M A G E  M O D A L  C O N T R O L S -->
        <div class="flex items-center absolute top-20 right-20 z-100">
          <!-- M O R E  D R O P D O W N -->
          <DropdownModule
            icon="icon-more-horizontal text-30 text-white"
            class="flex mr-10"
            dropdown-z-index="z-100"
            :width="user || share.mode === 'write' ? 148 : 111"
            background="bg-white image-modal-more-menu"
            remove-optional-top-gap
            button-opacity="80"
            dropdown-menu-position="fixed"
          >
            <!-- L I N K  B U T T O N -->
            <ButtonModule
              v-if="user || share.mode === 'write'"
              ref="linkBtn"
              prefix="icon-link text-17 text-grey-main"
              class="py-13 px-10 relative"
              has-hover
              tooltip-text="add link"
              tooltip-position="bottom-40"
              tooltip-size="11"
              :button-opacity="node.attrs.imageLink || isModalMenuLinkActive ? '100' : '80'"
              @click.stop="showLinkMenu"
            />

            <!-- C H A T  B U T T O N -->
            <ButtonModule
              ref="commentBtn"
              prefix="icon-chat text-17 text-grey-main"
              class="py-13 px-10 relative"
              has-hover
              tooltip-text="add comment"
              tooltip-position="bottom-40"
              tooltip-size="11"
              :button-opacity="node.attrs.imageComment || isModalMenuCommentActive ? '100' : '80'"
              @click.stop="showComment"
            />

            <!-- O P E N  I M A G E  I N  N E W  T A B -->
            <ButtonModule
              prefix="icon-image text-17 text-grey-main"
              class="py-13 px-10 relative"
              has-hover
              tooltip-text="original"
              tooltip-position="bottom-40"
              tooltip-size="11"
              button-opacity="80"
              @click="openOriginalImage"
            />

            <!-- D O W N L O A D -->
            <ButtonModule
              prefix="icon-download text-17 text-grey-main"
              class="py-13 px-10 relative"
              has-hover
              tooltip-text="download"
              tooltip-position="bottom-40"
              tooltip-size="11"
              button-opacity="80"
              @click="downloadImage"
            />

            <!-- C O M M E N T  F O R M -->
            <CommentForm
              v-if="isModalMenuCommentActive"
              ref="commentRef"
              :key="project.id"
              :comment-id="+node.attrs.imageComment"
              comment-position="right: 0; top: 44px"
              @setComment="setComment"
              @hideComment="hideComment"
            />

            <!-- L I N K  F O R M -->
            <div
              v-show="isModalMenuLinkActive"
              ref="link"
              class="flex items-center absolute z-9999 shadow-dropdown"
              :style="linkPosition"
            >
              <input
                ref="linkInputRef"
                v-model="linkUrl"
                class="py-8 px-20 outline-none lowercase h-40 bg-primary text-app-color"
                type="text"
                placeholder="https://"
                @click.stop
                @keydown.esc="hideLinkMenu"
                @keydown.enter="toggleLinkUrl(linkUrl)"
              >
              <button v-if="node.attrs.imageLink" class="text-13 text-grey-300 w-40 h-40 bg-primary rounded-r-4" type="button" @click="toggleLinkUrl(null)">
                <i class="icon-close" />
              </button>
            </div>
          </DropdownModule>

          <!-- C L O S E  M O D A L  B U T T O N -->
          <ButtonModule
            prefix="icon-close mr-15"
            :has-hover="true"
            class="text-18 text-app-color cursor-pointer text-white"
            button-opacity="80"
            @click="closeModal"
          />
        </div>

        <img
          :src="url"
          class="h-1/3 md:h-2/3 max-h-full"
          alt=""
        >
      </div>
    </transition>
  </teleport>
</template>

<script>
import { defineComponent, ref, reactive, computed, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import DropdownModule from '@/components/shared/form-controls/DropdownModule'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import CommentForm from '@/components/comments/CommentForm.vue'
import { commentService } from '@/services'

export default defineComponent({
  name: 'ImageModal',

  components: {
    DropdownModule,
    ButtonModule,
    CommentForm
  },

  props: {
    node: Object,
    nodePos: Number,
    editor: Object,
    isModalMenuLinkActive: Boolean,
    isModalMenuCommentActive: Boolean
  },

  emits: ['input', 'toggle-modal-menu-link', 'toggle-modal-menu-comment'],

  setup (props, { emit }) {
    const { state, getters, commit } = useStore()
    const route = useRoute()

    const linkInputRef = ref(null)
    const commentRef = ref(null)
    const linkUrl = ref(null)
    const isImageModalOpen = ref(false)
    const linkPosition = reactive({ right: 0, top: '44px' })

    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const project = computed(() => state.project.currentProject)
    const share = computed(() => state.project.share)
    const user = computed(() => state.profile.user)
    const url = computed(() => props.node.attrs.src)

    function open () {
      isImageModalOpen.value = true
    }

    function closeModal () {
      isImageModalOpen.value = false
    }

    function openOriginalImage () {
      const image = new Image()
      image.src = url.value
      window.open(url.value, '_blank')
    }

    function downloadImage () {
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url.value
      document.body.append(a)
      a.click()
      document.body.removeChild(a)
    }

    // L I N K  L O G I C
    function showLinkMenu () {
      hideComment()
      linkUrl.value = props.node.attrs.imageLink
      emit('toggle-modal-menu-link', !props.isModalMenuLinkActive)

      if (props.isModalMenuLinkActive) {
        nextTick(() => {
          linkInputRef.value.focus()
        })
      }
    }

    function hideLinkMenu () {
      linkUrl.value = 'https://'
      emit('toggle-modal-menu-link', false)
    }

    function parseUrl (url) {
      const splitUrl = url.split('://')
      if (splitUrl.length > 1) return url

      return `https://${url}`
    }

    function toggleLinkUrl (url) {
      if (url) {
        const href = parseUrl(url)
        props.editor.commands.addLink(href, props.nodePos)
      } else {
        props.editor.commands.addLink(null, props.nodePos)
      }
      hideLinkMenu()
    }

    // C O M M E N T  L O G I C
    function showComment () {
      hideLinkMenu()
      emit('toggle-modal-menu-comment', !props.isModalMenuCommentActive)

      if (!props.isModalMenuCommentActive) {
        setTimeout(() => {
          commentRef.value.setFocus()
        }, 100)
      }
    }

    function setComment (value) {
      const id = props.node.attrs.imageComment

      if (value && !id) {
        createComment(value)
      } else if (value && id) {
        updateComment(value, id)
      } else if (id) {
        deleteComment(id)
      }

      hideComment()
    }

    function createComment (value) {
      if (route.name === 'Share') {
        commentService.createWithShare({ body: value }, project.value.id, share.value.id)
          .then(({ data }) => {
            // save comment to store
            const comments = project.value.comments
            comments.push(data.dataValues)
            commit('updateComments', comments)

            props.editor.commands.addComment(data.dataValues.id, props.nodePos)
          })
      } else {
        commentService.create({ body: value }, project.value.id)
          .then(({ data }) => {
            const comments = project.value.comments
            const comment = {
              ...data.dataValues,
              user: user.value
            }

            comments.push(comment)
            commit('updateComments', comments)

            props.editor.commands.addComment(data.dataValues.id, props.nodePos)
          })
      }
    }

    function updateComment (value, id) {
      commentService.update(id, value)
        .then(() => {
          const comments = project.value.comments
          const comment = comments.find(item => item.id === +id)
          comment.body = value
          commit('updateComments', comments)
        })
    }

    function deleteComment (id) {
      commentService.delete(id).then(() => {
        commit('updateComments', project.value.comments.filter(item => item.id !== id))

        props.editor.commands.addComment(null, props.nodePos)
      })
    }

    function hideComment () {
      emit('toggle-modal-menu-comment', false)
    }

    return {
      // refs
      commentRef,
      linkInputRef,
      linkUrl,
      isImageModalOpen,
      linkPosition,
      // computed
      isMobile,
      isDarkMode,
      project,
      share,
      user,
      url,
      // function
      open,
      closeModal,
      openOriginalImage,
      downloadImage,
      showLinkMenu,
      hideLinkMenu,
      toggleLinkUrl,
      showComment,
      setComment,
      hideComment
    }
  }
})
</script>
